import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { formatDataQaIds } from 'utils';

import BonusPoints from 'components/bonus-points';
import Incrementor from 'components/incrementor';
import Picture from 'components/picture';
import NullImageFallBack from 'components/picture/null-picture-fallback';
import { Calories } from 'components/price-and-calories';
import SelectionIndicator from 'components/selection-indicator';
import { Size } from 'components/selection-indicator/constants';
import { useFormatCalories } from 'hooks/use-format-calories';
import { useUIContext } from 'state/ui';

import { IComboSlotOptionProps } from '../types';

import ComboSlotOptionCardDetail from './combo-slot-option-card-detail';
import OptionNutrition from './combo-slot-option-nutrition';
import OptionTitle from './combo-slot-option-title';
import {
  CheckboxContainer,
  ComboSelectionDetails,
  ComboSelectionPrice,
  ComboSlotOptionLabel,
  ComboSlotOptionWrapper,
  ImageContainer,
} from './styled';

const ComboSlotOption: React.FC<IComboSlotOptionProps> = ({
  option,
  item,
  extraCharge,
  isReadOnly,
  onChange,
  handleQuantityUpdate,
  maxAmount = 1,
  respectMaximum = false,
}) => {
  const calories = option?.nutrition?.calories || 0;
  const locale = option?.name?.locale || '';
  const { formatMessage } = useIntl();
  const formatCalories = useFormatCalories();
  const { formatCurrencyForLocale } = useUIContext();

  const handleSelectionChange = () => {
    onChange(respectMaximum);
  };

  const handleQuantityChange = useCallback(
    (value: number) => {
      handleQuantityUpdate(option, value);
    },
    [option, handleQuantityUpdate]
  );
  const checked = Boolean(item && item?.quantity);
  const hasImage = option.image && option.image.asset;
  const isEditable = !isReadOnly;
  const shouldDisplayIncrementor = respectMaximum ? maxAmount > 1 : isEditable;
  const shouldNotUpsell = respectMaximum || extraCharge <= 0;
  const itemMaxAmount = respectMaximum ? maxAmount : item?.option?.maxAmount;
  const shouldDisplayRadioButton = respectMaximum && maxAmount === 1;
  // If a user increments the quantity of this combo slot,
  // we need to legally show the caloric intake for the amount
  // they are ordering. thus multiplying it by the quantity
  const caloriesbyQuantity: number = calories * (item?.quantity || 1);

  return (
    <ComboSlotOptionWrapper data-testid="combo-slot">
      <ComboSlotOptionLabel>
        {/* Don't show placeholder image if the item is read only and doesn't have an image */}
        {(hasImage || isEditable) && (
          <ImageContainer data-testid="combo-slot-image">
            {hasImage ? (
              <Picture image={option.image} alt={option.name?.locale} />
            ) : (
              <NullImageFallBack />
            )}
          </ImageContainer>
        )}

        <ComboSlotOptionCardDetail>
          <OptionTitle>{option.name?.locale}</OptionTitle>
          <OptionNutrition>
            <Calories>{formatCalories(caloriesbyQuantity)}</Calories>
          </OptionNutrition>
          {option?.promotion?.bonusPoints && (
            <BonusPoints bonusPoints={option.promotion.bonusPoints} />
          )}
        </ComboSlotOptionCardDetail>

        {isEditable && (
          <CheckboxContainer data-testid={`combo-slot-${formatDataQaIds(locale)}`}>
            <SelectionIndicator
              type={shouldDisplayRadioButton ? 'radio' : undefined}
              size={Size.REGULAR}
              checked={checked}
              onChange={handleSelectionChange}
              disabled={isReadOnly}
            />
          </CheckboxContainer>
        )}
      </ComboSlotOptionLabel>

      {checked && shouldDisplayIncrementor && (
        <ComboSelectionDetails data-testid="combo-selection-details">
          <Incrementor
            aria-label={`Quantity ${item?.quantity} of ${option.name?.locale}`}
            name={option.name?.locale}
            value={item?.quantity || 0}
            onChange={handleQuantityChange}
            min={1}
            max={itemMaxAmount}
          >
            <ComboSelectionPrice extraCharge={extraCharge > 0}>
              {shouldNotUpsell
                ? formatMessage({ id: 'included' })
                : `+${formatCurrencyForLocale(extraCharge)}`}
            </ComboSelectionPrice>
          </Incrementor>
        </ComboSelectionDetails>
      )}
    </ComboSlotOptionWrapper>
  );
};

export default ComboSlotOption;
